// eslint-disable-next-line no-restricted-imports
import styled from '@emotion/styled';
import { Flex } from '@iheartjane/reefer';
import type { FlexProps } from '@iheartjane/reefer';
import { useEffect, useState } from 'react';
import type { PropsWithChildren } from 'react';
import { Outlet } from 'react-router';

import type { RootLoaderType } from '@jane/kiosk/data-access';
import { useGetWhoami } from '@jane/kiosk/data-access';
import { useKioskSpacing, useTypedRouteLoaderData } from '@jane/kiosk/hooks';
import {
  useBrandSpecials,
  useIdleContext,
  useModalsDisplayedContext,
  useResetKiosk,
} from '@jane/kiosk/providers';

import { BagAlert } from '../BagAlert/BagAlert';
import { IdlePrompt } from '../IdlePrompt/IdlePrompt';
import { KioskPassword } from '../KioskPassword/KioskPassword';

interface LayoutPropsInternal extends Pick<
  FlexProps,
  'alignItems' | 'justifyContent'
> {
  backgroundColor?: string;
  id?: string;
  isFullScreen?: boolean;
  passwordProtected?: boolean;
}

export type LayoutProps = PropsWithChildren<LayoutPropsInternal>;

const Wrapper = styled(Flex)<LayoutProps>(({
  isFullScreen,
  backgroundColor,
  theme,
}) => {
  return {
    ...(isFullScreen
      ? {
          backgroundColor: backgroundColor ?? theme.colors.background,
          minHeight: window.innerHeight,
          minWidth: window.innerWidth,
        }
      : {}),
  };
});

export const Layout = ({
  alignItems,
  children,
  id,
  isFullScreen = false,
  justifyContent,
  passwordProtected = true,
  backgroundColor,
}: LayoutProps) => {
  const { whoami: initialWhoami } =
    useTypedRouteLoaderData<RootLoaderType>('root');
  const { data: whoami, isSuccess: isWhoamiSuccess } = useGetWhoami({
    initialData: initialWhoami,
  });
  const { shouldShowAlert } = useBrandSpecials();
  const { loginModalDisplayed } = useModalsDisplayedContext();
  const isAuthenticated = !passwordProtected || (isWhoamiSuccess && whoami?.id);
  const { activate, isPromptVisible, reset } = useIdleContext();
  const { isResetting } = useResetKiosk();
  const { isIpad } = useKioskSpacing();
  const isUnauthenticatedAndOnIPad = !isAuthenticated && isIpad;

  const [isBagAlertVisible, setIsBagAlertVisible] = useState(false);
  useEffect(() => {
    if (shouldShowAlert) {
      setIsBagAlertVisible(true);
    }
  }, [shouldShowAlert]);

  return (
    <Wrapper
      alignItems={alignItems || 'center'}
      backgroundColor={isAuthenticated ? backgroundColor : '#fff'}
      flexDirection="column"
      id={id}
      isFullScreen={isFullScreen || !isAuthenticated}
      justifyContent={
        justifyContent || (isUnauthenticatedAndOnIPad ? 'flex-start' : 'center')
      }
      mt={isUnauthenticatedAndOnIPad ? 128 : undefined}
    >
      {!isAuthenticated && <KioskPassword />}
      {isAuthenticated && (
        <>
          {children}
          <Outlet />
          <IdlePrompt
            isDeletingCart={isResetting}
            isOpen={isPromptVisible}
            onContinue={activate}
            onReset={reset}
          />
          <BagAlert
            isOpen={
              isBagAlertVisible && !isPromptVisible && !loginModalDisplayed
            }
            onRequestClose={() => setIsBagAlertVisible(false)}
          />
        </>
      )}
    </Wrapper>
  );
};
